import { zodResolver } from '@hookform/resolvers/zod';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { useCursorSelect } from '@/hooks/use-cursor-select';

import { listReportCustomers } from '../../api';
import { reportKeys } from '../../hooks';
import { isNumericKey, stripToNumeric } from '../../numeric-input';
import type { ReportOption } from '../../types';
import { toDefaults, toFilters } from '../filters';
import { subscriptionsReportFiltersSchema } from '../schemas';
import type { SubscriptionsReportFilterFormValues, SubscriptionsReportListFilters } from '../types';

import styles from './subscriptions-filter-drawer.module.scss';

type Draft = SubscriptionsReportFilterFormValues;

const customerToOption = (c: ReportOption) => ({ value: c.id, label: c.name });

/** The four numeric bound paths — the only ones `countGuard` may drive. Narrowing to these (rather
 *  than any `FieldPath<Draft>`) is what makes the paste handler's `setValue(name, number | null)`
 *  well-typed, and keeps `customerIds` out of a numeric handler. Same shape as the Device Activity
 *  drawer's `NumericBound`. */
type NumericBound = {
  [K in keyof Draft]-?: Draft[K] extends number | null ? K : never;
}[keyof Draft];

/** AC8: a non-numeric character must be rejected **as it is typed** and stripped from a paste. antd
 *  6's `<InputNumber>` does neither on its own — probed 2026-08-07: typing `a1b2c` leaves the
 *  literal `a1b2c` on screen and pasting `12abc34` leaves `12abc34`. Both guards come from the
 *  shared `numeric-input` helpers ticket 07 introduced; `allowDecimal` is `false` here because both
 *  metrics on this panel are whole counts (a Customer's device count and its expiring-in-30-days
 *  count), unlike Device Activity's Avg runtime/day.
 *
 *  A paste into a range box is a whole value in practice, so the stripped text replaces the field
 *  rather than splicing at the caret. */
const countGuard = (name: NumericBound, setValue: UseFormSetValue<Draft>) => ({
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
    // Never swallow a shortcut (Ctrl+A, Cmd+V, …) — only bare printable characters.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (!isNumericKey(event.key, false)) event.preventDefault();
  },
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const clean = stripToNumeric(event.clipboardData.getData('text'), false);
    // A paste with no usable digits clears the bound rather than writing `NaN`, which would reach
    // zod as an unrepresentable value.
    setValue(name, clean === '' ? null : Number(clean), { shouldValidate: true });
  },
});

/** `min: 0` because a Customer may legitimately own **0** devices — unlike the Orders drawer's
 *  `min: 1` (an order always has at least one item). `precision: 0` rejects a decimal point. */
const COUNT_PROPS = { min: 0, precision: 0 } as const;

export type SubscriptionsFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: SubscriptionsReportListFilters;
  onApply: (filters: SubscriptionsReportListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<SubscriptionsFilterDrawerProps, 'open'>;

/**
 * The smallest filter panel in the Reports module: one async multi-select and two numeric ranges,
 * in the **story's** order — Customers, Total Number of Devices, Expiring in 30 Days.
 *
 * Figma 32:190248 orders the last two the other way round and labels the second "Device Expiring in
 * 30 Days". Settled in grilling Q3: the panel must agree with the table, whose columns ticket 08
 * shipped in this same story order, and the label must match the column header it filters. Both are
 * on the non-blocking design note owed to the designer.
 */
function FilterBody({ value, onApply, onClose }: BodyProps) {
  const form = useForm<Draft>({
    resolver: zodResolver(subscriptionsReportFiltersSchema),
    // No `?? null` coalescing here, unlike the Orders and Warranty drawers. That dance exists for
    // `enumField` DATE bounds, which `toDefaults` maps to `undefined`; every field on this panel is
    // a multi (→ `[]`) or a numeric range (→ `null`), which `toDefaults` already returns controlled.
    defaultValues: toDefaults(value),
    // onChange (like the Customers drawer, not Orders' onBlur) so the range error and the
    // Apply-disable react as the admin types — the AC prevents Apply "until corrected".
    mode: 'onChange',
  });

  // Cursor-paginated, server-searched typeahead scoped to Customers who own at least one product:
  // scrolling the popup loads the next page, and a new term swaps the query key so the cursor
  // resets to the first page (see `useCursorSelect`). The cursor stays opaque to the FE.
  const customerSelect = useCursorSelect({
    queryKey: reportKeys.metaCustomers,
    fetchPage: listReportCustomers,
    toOption: customerToOption,
  });

  // Single source for the To >= From rule: the schema owns it and attaches each issue to the `To`
  // path, so Apply is disabled straight off the validation state rather than re-deriving it here.
  const rangeInvalid = Boolean(
    form.formState.errors.totalDevicesTo || form.formState.errors.expiringIn30DaysTo,
  );

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  // No `width`: the shared Drawer already defaults to 464px, which is Figma 32:190248 exactly.
  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={rangeInvalid}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        // Plural, per Figma and the shipped Orders drawer: the column reads "Customer", the filter
        // offers several. No `emptyText` — antd's default empty panel; no Reports frame specifies
        // the designed one.
        label="Customers"
        placeholder="Select"
        showSearch={customerSelect.showSearch}
        loading={customerSelect.loading}
        onPopupScroll={customerSelect.onPopupScroll}
        options={customerSelect.options}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Total Number of Devices</legend>
        <div className={styles.row}>
          <NumberField
            name="totalDevicesFrom"
            control={form.control}
            aria-label="Total Number of Devices From"
            placeholder="From"
            {...COUNT_PROPS}
            {...countGuard('totalDevicesFrom', form.setValue)}
          />
          <NumberField
            name="totalDevicesTo"
            control={form.control}
            aria-label="Total Number of Devices To"
            placeholder="To"
            {...COUNT_PROPS}
            {...countGuard('totalDevicesTo', form.setValue)}
          />
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Expiring in 30 Days</legend>
        <div className={styles.row}>
          <NumberField
            name="expiringIn30DaysFrom"
            control={form.control}
            aria-label="Expiring in 30 Days From"
            placeholder="From"
            {...COUNT_PROPS}
            {...countGuard('expiringIn30DaysFrom', form.setValue)}
          />
          <NumberField
            name="expiringIn30DaysTo"
            control={form.control}
            aria-label="Expiring in 30 Days To"
            placeholder="To"
            {...COUNT_PROPS}
            {...countGuard('expiringIn30DaysTo', form.setValue)}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function SubscriptionsFilterDrawer({ open, ...rest }: SubscriptionsFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`
  // (which is also why `useCursorSelect`'s `resetSearch` is not needed — a stale search term cannot
  // survive a remount).
  return <FilterBody {...rest} />;
}
